import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { importsAPI } from '../../lib/api';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Check, X, AlertTriangle, RotateCcw, Loader2, FileSpreadsheet, Pencil, Save
} from 'lucide-react';

const STATUS_CONFIG = {
  success: { label: 'Success', variant: 'default', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  failed: { label: 'Failed', variant: 'destructive', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  corrected: { label: 'Corrected', variant: 'default', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  pending: { label: 'Pending', variant: 'secondary', className: 'bg-gray-100 text-gray-600' },
};

const ENTITY_LABELS = {
  inventory_items: 'Inventory Items',
  price_book: 'Price Book',
  vehicles: 'Vehicles',
  warehouses: 'Warehouses',
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cfg.className}`}>{cfg.label}</span>;
}

function EditableRow({ row, onSave }) {
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const data = row.corrected_data_json || row.data_json;
  const fields = Object.keys(row.data_json);
  const errorFields = (row.error_json || []).map(e => e.field);

  const startEdit = () => {
    setEditData({ ...data });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await importsAPI.correctRow(row.id, editData);
      toast.success(`Row ${row.row_number} corrected`);
      setEditing(false);
      onSave();
    } catch (err) {
      toast.error('Failed to save correction');
    } finally {
      setSaving(false);
    }
  };

  if (row.status === 'success' || row.status === 'corrected') {
    return (
      <tr className="border-b border-border/50" data-testid={`import-row-${row.row_number}`}>
        <td className="px-3 py-2 text-sm font-mono text-muted-foreground">{row.row_number}</td>
        <td className="px-3 py-2"><StatusBadge status={row.status} /></td>
        <td className="px-3 py-2 text-sm text-muted-foreground" colSpan={fields.length + 1}>
          {row.status === 'corrected' ? 'Re-imported successfully' : 'Imported successfully'}
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-border/50 hover:bg-muted/30" data-testid={`import-row-${row.row_number}`}>
      <td className="px-3 py-2 text-sm font-mono text-muted-foreground">{row.row_number}</td>
      <td className="px-3 py-2"><StatusBadge status={row.status} /></td>
      {fields.map(field => {
        const hasError = errorFields.includes(field);
        const val = editing ? editData[field] || '' : (data[field] || '');
        return (
          <td key={field} className="px-3 py-2">
            {editing ? (
              <Input
                value={val}
                onChange={e => setEditData(prev => ({ ...prev, [field]: e.target.value }))}
                className={`h-7 text-xs ${hasError ? 'border-red-400 bg-red-50 dark:bg-red-950/20' : ''}`}
                data-testid={`edit-${row.row_number}-${field}`}
              />
            ) : (
              <span className={`text-sm ${hasError ? 'text-red-600 font-medium' : ''}`}>
                {data[field] || <span className="text-muted-foreground italic">empty</span>}
              </span>
            )}
          </td>
        );
      })}
      <td className="px-3 py-2">
        {row.error_json && (
          <div className="space-y-0.5">
            {row.error_json.map((e, i) => (
              <div key={i} className="text-xs text-red-600 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 shrink-0" />
                <span><strong>{e.field}:</strong> {e.message}</span>
              </div>
            ))}
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        {editing ? (
          <div className="flex gap-1">
            <Button size="sm" className="h-7 gap-1 text-xs" onClick={handleSave} disabled={saving} data-testid={`save-row-${row.row_number}`}>
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Save
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        ) : row.status === 'failed' ? (
          <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={startEdit} data-testid={`edit-row-${row.row_number}`}>
            <Pencil className="w-3 h-3" /> Edit
          </Button>
        ) : null}
      </td>
    </tr>
  );
}

export default function ImportReview() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [reimporting, setReimporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const statusParam = filter === 'all' ? undefined : filter;
      const res = await importsAPI.getSession(sessionId, statusParam);
      setSession(res.data.session);
      setRows(res.data.rows);
    } catch (err) {
      toast.error('Failed to load import session');
    } finally {
      setLoading(false);
    }
  }, [sessionId, filter]);

  useEffect(() => { load(); }, [load]);

  const handleReimport = async () => {
    setReimporting(true);
    try {
      const res = await importsAPI.reimport(sessionId);
      toast.success(`Re-imported ${res.data.succeeded} rows`, {
        description: res.data.still_failed > 0 ? `${res.data.still_failed} still have errors` : 'All corrected rows imported successfully',
      });
      load();
    } catch (err) {
      toast.error('Re-import failed', { description: err.response?.data?.detail || 'Please try again' });
    } finally {
      setReimporting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
      </Layout>
    );
  }

  if (!session) {
    return (
      <Layout><div className="p-6 text-center text-muted-foreground">Session not found</div></Layout>
    );
  }

  const fields = rows.length > 0 ? Object.keys(rows[0].data_json) : [];
  const correctedCount = rows.filter(r => r.status === 'failed' && r.corrected_data_json).length;

  return (
    <Layout>
    <div className="space-y-4" data-testid="import-review-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="import-review-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg font-semibold flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-primary" />
              Import Review
            </h1>
            <p className="text-sm text-muted-foreground">
              {session.filename || 'CSV Import'} · {ENTITY_LABELS[session.entity_type] || session.entity_type}
            </p>
          </div>
        </div>
        {correctedCount > 0 && (
          <Button onClick={handleReimport} disabled={reimporting} className="gap-2" data-testid="reimport-btn">
            {reimporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            Re-import {correctedCount} Corrected Row{correctedCount > 1 ? 's' : ''}
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="overflow-hidden">
          <div className="h-[3px] bg-muted-foreground" />
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold font-mono">{session.total_rows}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Rows</p>
          </CardContent>
        </Card>
        <Card className="overflow-hidden">
          <div className="h-[3px] bg-emerald-500" />
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold font-mono text-emerald-600">{session.success_count}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Imported</p>
          </CardContent>
        </Card>
        <Card className="overflow-hidden">
          <div className="h-[3px] bg-red-500" />
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold font-mono text-red-600">{session.failed_count}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Failed</p>
          </CardContent>
        </Card>
        <Card className="overflow-hidden">
          <div className="h-[3px] bg-blue-500" />
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold font-mono text-blue-600">{session.corrected_count}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Corrected</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 border-b border-border pb-1">
        {[
          { key: 'all', label: 'All' },
          { key: 'failed', label: `Failed (${session.failed_count})` },
          { key: 'success', label: `Success (${session.success_count})` },
          { key: 'corrected', label: `Corrected (${session.corrected_count})` },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-t transition-colors ${
              filter === tab.key ? 'bg-primary/10 text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`filter-${tab.key}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Rows table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-3 py-2 text-xs font-semibold text-muted-foreground w-12">Row</th>
                <th className="px-3 py-2 text-xs font-semibold text-muted-foreground w-20">Status</th>
                {fields.map(f => (
                  <th key={f} className="px-3 py-2 text-xs font-semibold text-muted-foreground capitalize">
                    {f.replace(/_/g, ' ')}
                  </th>
                ))}
                <th className="px-3 py-2 text-xs font-semibold text-muted-foreground">Errors</th>
                <th className="px-3 py-2 text-xs font-semibold text-muted-foreground w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <EditableRow key={row.id} row={row} onSave={load} />
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No rows match this filter
          </div>
        )}
      </Card>
    </div>
    </Layout>
  );
}
